import React from 'react'

import dynamic from 'next/dynamic'
import {GetServerSideProps} from 'next'
import fromPairs from 'lodash/fromPairs'
import {ParsedUrlQuery} from 'querystring'
import urljoin from 'url-join';

import {handleRestCache} from '@festi/common/api/global-cache'
import {Category, restApi} from '@festi/common/api/rest'
import settings from '@festi/common/constants/settings'
import {getMetaDescription} from '@festi/common/utils/seo'
import {onDutyFree} from '@festi/common/constants/channels'

import {WebPage} from 'src/components/layout'
import {WebPageCommonProps} from 'src/components/layout/WebPage'
import {makePageCommonProps} from 'src/utils'

const SearchResults = dynamic(
  () => import('src/components/search/SearchResults'),
)

const CategoryResults = dynamic(
  () => import('src/components/search/redesign/CategoryResults'),
)

export const getServerSideProps: GetServerSideProps = async ({
  res,
  query,
  preview = false,
}) => {
  const common = await makePageCommonProps(preview)

  const fieldTypes = await handleRestCache(`fieldtype_list`, () =>
    restApi.fieldTypesList(),
  )

  const attrNameMap = fromPairs(fieldTypes.map((t) => [t.key, t.label]))
  if (common.categories.length === 0) {
    res.setHeader('Cache-Control', 's-maxage=1, stale-while-revalidate')
  } else {
    res.setHeader('Cache-Control', 's-maxage=60, stale-while-revalidate=3600')
  }

  return {
    props: {
      query,
      common,
      attrNameMap,
      preview,
    },
  }
}

interface Props {
  common: WebPageCommonProps
  query: ParsedUrlQuery
  ancestry: Category[]
  attrNameMap: Record<string, string>
}

export default function Leit({common, query, attrNameMap}: Props): JSX.Element {
  
  const searchTerm = typeof query.q === 'string' ? query.q.trim() : '';
  const searchTermLower = searchTerm.toLowerCase();
  const title = searchTerm
    ? `Vöruleit fyrir „${searchTermLower}“`
    : 'Vöruleit';
  const description = getMetaDescription({searchTermLower});
  const baseUrl = onDutyFree ? settings.dutyfreeUrl : settings.baseUrl;
  const canonicalUrl = searchTerm
  ? urljoin(baseUrl, '/leit?q=' + encodeURIComponent(searchTermLower))
  : baseUrl;

  return (
    <WebPage
      common={common}
      attrNameMap={attrNameMap}
      nextSeo={{
        seoTitle: title,
        seoDescription: description,
        seoCanonical: canonicalUrl,
      }}
    >
      {settings.redesign ? (
        <CategoryResults
          title="Vöruleit"
          attrNameMap={attrNameMap}
          initialQuery={query}
        />
      ) : (
        <SearchResults
          title="Vöruleit"
          common={common}
          attrNameMap={attrNameMap}
        />
      )}
    </WebPage>
  );
}
